/**
 * PriceExplorer — a simple search box for purchase-history lookup.
 * User types an item code or name, we call /api/price-explorer/search and
 * render a card per matching item showing:
 *   • Lowest cost (last 180d) + which vendor quoted it
 *   • Last (most recent) cost + vendor
 * Read-only, safe for any authenticated user. Linked from Home.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Home, ArrowLeft, TrendingDown, TrendingUp, Minus, Clock, IndianRupee, Loader2, BarChart3, Layers, Info } from "lucide-react";
import { toast } from "sonner";
import { PriceHistoryDrawer } from "@/components/PriceHistoryDrawer";

// One place to keep currency formatting consistent with the rest of the app.
const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 2 }).format(Number(n) || 0);

// Compact date renderer — the API returns ISO strings.
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

// Price-velocity classifier — compares WMA against flat qty-weighted average.
// The gap tells us whether recent bills are drifting up/down vs the long-term
// baseline. We treat ±3% as "stable" (below noise) and only flag beyond that,
// so genuine trend signals stand out and the buyer isn't distracted by tiny
// fluctuations. Also require ≥5 bills of history so a noisy 2-bill sample
// doesn't fake a "trending up" alarm.
const classifyVelocity = (avgRate, wmaRate, billCount) => {
  if (!avgRate || !wmaRate || avgRate <= 0) return null;
  if ((billCount || 0) < 5) return null;  // too little data to trust a trend
  const deltaPct = ((wmaRate - avgRate) / avgRate) * 100;
  if (Math.abs(deltaPct) < 3) {
    return { tone: "stable", deltaPct, label: "Stable", tip: "Recent bills within ±3% of the long-term average." };
  }
  if (deltaPct > 0) {
    return {
      tone: "up", deltaPct,
      label: `+${deltaPct.toFixed(1)}%`,
      tip: "Recent bills trending UP vs. the long-term average — consider timing the PR carefully or negotiating hard.",
    };
  }
  return {
    tone: "down", deltaPct,
    label: `${deltaPct.toFixed(1)}%`,
    tip: "Recent bills trending DOWN vs. the long-term average — good time to lock in a PR.",
  };
};

// Freshness label — turns a minutes int into a compact "3h ago" pill string.
const fmtStaleness = (mins) => {
  if (mins == null) return "unknown";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

// Contract validity — 9999-12-31 is SAP's "open-ended" sentinel; hide it
// so the UI shows "no end" cleanly rather than a nonsensical year.
const fmtContractDate = (iso) => {
  if (!iso) return "—";
  if (String(iso).startsWith("9999")) return "no end";
  return fmtDate(iso);
};

// -------------------------------------------------------------------
// SupplierCompareCard — one row per supplier for a single item, showing
// MS SQL billed-history stats (last / lowest / bill count) and the
// SAP standing list-price contract side by side. Source pills at page
// level hide columns/rows the buyer doesn't care about.
// -------------------------------------------------------------------
function SupplierCompareCard({ data, sourceFilter, onOpenHistory }) {
  const rows = (data?.suppliers || []).filter((r) => {
    if (sourceFilter === "mssql") return !!r.mssql;
    if (sourceFilter === "sap")   return !!r.sap;
    return true;
  });
  // Cheapest last-rate across MS SQL rows — used to render a subtle
  // "best" badge so buyers can spot the winner in one glance.
  const bestMssql = rows.reduce((min, r) => {
    const lr = r.mssql?.last_rate;
    return (lr != null && (min == null || lr < min)) ? lr : min;
  }, null);
  const bestSap = rows.reduce((min, r) => {
    const a = r.sap?.amount;
    return (a != null && (min == null || a < min)) ? a : min;
  }, null);

  return (
    <Card className="border-slate-200" data-testid="pe-supplier-compare-card">
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-wrap items-baseline gap-2 mb-3">
          <span className="font-mono text-sm font-bold text-cyan-800">{data.icode}</span>
          <span className="text-sm text-slate-700 flex-1 truncate">{data.iname || "(no description)"}</span>
          <span className="text-fiori-caption text-slate-400">
            {rows.length} supplier{rows.length === 1 ? "" : "s"} · last {data.lookback_days}d
          </span>
        </div>
        {rows.length === 0 && (
          <div className="text-xs text-slate-500 italic py-6 text-center"
               data-testid="pe-supplier-empty">
            No supplier data for this item under the current filter.
          </div>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs border border-slate-100 rounded">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="text-left px-2 py-1.5 font-semibold">Supplier</th>
                  {sourceFilter !== "sap" && (
                    <>
                      <th className="text-right px-2 py-1.5 font-semibold text-cyan-800">MS SQL · Last</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-cyan-800 hidden sm:table-cell">Lowest</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-cyan-800 hidden sm:table-cell">Bills</th>
                    </>
                  )}
                  {sourceFilter !== "mssql" && (
                    <>
                      <th className="text-right px-2 py-1.5 font-semibold text-indigo-800">SAP · Contract</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-indigo-800 hidden sm:table-cell">Valid to</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-indigo-800 hidden sm:table-cell">Status</th>
                    </>
                  )}
                  <th className="text-right px-2 py-1.5 font-semibold text-slate-700">Receipts</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.supplier_code}-${i}`}
                      className="border-t border-slate-100 hover:bg-cyan-50/30"
                      data-testid={`pe-supplier-row-${r.supplier_code}`}>
                    <td className="px-2 py-1.5 align-top">
                      <div className="font-mono text-fiori-caption font-bold text-slate-900">{r.supplier_code}</div>
                      <div className="text-fiori-caption text-slate-500 truncate max-w-[180px]">{r.supplier_name || "—"}</div>
                    </td>
                    {sourceFilter !== "sap" && (
                      <>
                        <td className="px-2 py-1.5 align-top text-right num">
                          {r.mssql?.last_rate != null ? (
                            <span className="font-semibold text-slate-900 inline-flex items-center gap-1">
                              {inr(r.mssql.last_rate)}
                              {bestMssql != null && r.mssql.last_rate === bestMssql && (
                                <span className="text-fiori-caption bg-emerald-100 text-emerald-800 border border-emerald-200 px-1 rounded font-bold">BEST</span>
                              )}
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                          {r.mssql?.last_date && (
                            <div className="text-fiori-caption text-slate-500">{fmtDate(r.mssql.last_date)}</div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top text-right num hidden sm:table-cell">
                          {r.mssql?.lowest_rate != null ? (
                            <span className="text-slate-700">{inr(r.mssql.lowest_rate)}</span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-2 py-1.5 align-top text-right num text-slate-600 hidden sm:table-cell">
                          {r.mssql?.bill_count || 0}
                        </td>
                      </>
                    )}
                    {sourceFilter !== "mssql" && (
                      <>
                        <td className="px-2 py-1.5 align-top text-right num">
                          {r.sap?.amount != null ? (
                            <span className="font-semibold text-slate-900 inline-flex items-center gap-1">
                              {r.sap.currency_code === "INR"
                                ? inr(r.sap.amount)
                                : `${Number(r.sap.amount).toLocaleString(undefined, { maximumFractionDigits: 4 })} ${r.sap.currency_code}`}
                              {bestSap != null && r.sap.amount === bestSap && (
                                <span className="text-fiori-caption bg-emerald-100 text-emerald-800 border border-emerald-200 px-1 rounded font-bold">BEST</span>
                              )}
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                          {r.sap && (
                            <div className="text-fiori-caption text-slate-500">/{r.sap.base_quantity} {r.sap.unit_code}</div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top text-left text-slate-600 hidden sm:table-cell">
                          {r.sap ? fmtContractDate(r.sap.valid_to) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-2 py-1.5 align-top hidden sm:table-cell">
                          {r.sap?.release_status === "5" ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-fiori-caption font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">Released</span>
                          ) : r.sap?.release_status === "3" ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-fiori-caption font-semibold bg-amber-100 text-amber-800 border border-amber-200">In prep</span>
                          ) : (
                            <span className="text-fiori-caption text-slate-400">{r.sap ? "—" : ""}</span>
                          )}
                        </td>
                      </>
                    )}
                    {/* Receipts — deep-link into the per-supplier bill
                        history via the shared PriceHistoryDrawer, which
                        already accepts a `pcode` filter. Hidden when the
                        supplier has no MSSQL history (SAP-only rows). */}
                    <td className="px-2 py-1.5 align-top text-right whitespace-nowrap">
                      {r.mssql?.bill_count ? (
                        <button
                          type="button"
                          onClick={() => onOpenHistory && onOpenHistory({
                            icode: data.icode,
                            iname: data.iname,
                            pcode: r.supplier_code,
                          })}
                          className="text-cyan-700 hover:text-cyan-900 font-semibold underline underline-offset-2"
                          data-testid={`pe-supplier-receipts-${r.supplier_code}`}
                          title={`View the last ${r.mssql.bill_count} billed receipt${r.mssql.bill_count === 1 ? "" : "s"} from ${r.supplier_name || r.supplier_code}`}>
                          {r.mssql.bill_count} bill{r.mssql.bill_count === 1 ? "" : "s"} →
                        </button>
                      ) : (
                        <span className="text-fiori-caption text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PriceExplorer() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);   // null = no search yet; [] = no matches
  const [error, setError] = useState("");

  // View mode — "item" (default cards) or "supplier" (per-supplier
  // comparison table for a single item). Both modes start from an item
  // search; supplier mode simply reshapes the results into a
  // side-by-side comparison across every vendor that ever billed the
  // item + any vendor with a standing SAP list-price contract.
  const [viewMode, setViewMode] = useState("item");
  // Source filter — pills next to the search box. Filters the returned
  // cards / rows client-side after the search runs. `both` shows all.
  const [sourceFilter, setSourceFilter] = useState("both");
  // Per-supplier data (supplier-mode only). Keyed by icode → response
  // body of /api/price-explorer/by-supplier. Fetched lazily when the
  // user opens supplier mode or picks a new suggestion in it.
  const [supplierData, setSupplierData] = useState(null);
  const [supplierLoading, setSupplierLoading] = useState(false);

  // SAP list-price contracts for the current result set. Keyed by icode →
  // list of rows (see /api/price-explorer/sap-prices). Fetched lazily
  // after the main MS SQL search resolves so the primary results paint
  // instantly and SAP fills in a beat later.
  const [sapByCode, setSapByCode] = useState({});
  const [sapMeta, setSapMeta] = useState(null);  // { last_synced_at, age_minutes }
  const [sapLoading, setSapLoading] = useState(false);

  // -------- Autosuggest state --------
  // `suggestions` holds up to ~10 icode/iname pairs matching the current query.
  // `showSuggest` toggles the dropdown; kept separate from the array so a
  // stale keystroke's late response doesn't re-open a dropdown the user has
  // already dismissed with Escape / blur.
  // `activeIdx` tracks arrow-key highlight (-1 = mouse-only mode).
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Purchase-history drawer — null when closed, `{icode, iname}` when open.
  // Held at page level so a single drawer instance handles all card openers.
  const [historyItem, setHistoryItem] = useState(null);

  const runSearch = useCallback(async (queryOverride) => {
    const query = (queryOverride ?? q).trim();
    if (query.length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    // Any explicit search closes the suggest dropdown.
    setShowSuggest(false);
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/price-explorer/search", {
        params: { q: query, limit: 25 },
      });
      const results = data?.items || [];
      setItems(results);
      // Fire the SAP list-price companion fetch alongside — deliberately
      // sequenced AFTER MS SQL results so the primary cards paint first.
      setSapByCode({});
      setSapMeta(null);
      const codes = results.map((r) => r.icode).filter(Boolean).slice(0, 20);
      if (codes.length) {
        setSapLoading(true);
        try {
          const { data: sap } = await api.get("/price-explorer/sap-prices", {
            params: { codes: codes.join(",") },
          });
          setSapByCode(sap?.items || {});
          setSapMeta({ last_synced_at: sap?.last_synced_at, age_minutes: sap?.age_minutes });
        } catch (_) {
          // Non-fatal — MS SQL results still stand on their own.
        } finally {
          setSapLoading(false);
        }
      }
      // Supplier-compare data — fetched for the FIRST result only, since
      // the supplier view focuses on a single item at a time. If the
      // search returns nothing this is a no-op.
      setSupplierData(null);
      if (results.length > 0 && results[0].icode) {
        setSupplierLoading(true);
        try {
          const { data: sup } = await api.get("/price-explorer/by-supplier", {
            params: { icode: results[0].icode },
          });
          setSupplierData(sup || null);
        } catch (_) {
          // Non-fatal; supplier mode will show an empty state.
        } finally {
          setSupplierLoading(false);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Search failed";
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q]);

  // Debounced typeahead. 250ms feels responsive without hammering the API on
  // every keystroke. We only fire when q has 2+ chars — one char produces a
  // near-useless full-catalogue scan.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggest(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/price-explorer/suggest", {
          params: { q: query, limit: 10 },
        });
        setSuggestions(data?.items || []);
        setShowSuggest((data?.items || []).length > 0);
        setActiveIdx(-1);
      } catch {
        // Silent — the main Search button still works, and a failing suggest
        // shouldn't spam toasts as the user types.
        setSuggestions([]);
        setShowSuggest(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Close the dropdown when the user clicks outside the wrapper.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Keyboard: Enter runs search (or picks a highlighted suggestion), Escape
  // dismisses, ArrowUp/Down move the highlight, Tab accepts the highlight.
  const handleKeyDown = (e) => {
    if (!showSuggest || suggestions.length === 0) {
      if (e.key === "Enter") { e.preventDefault(); runSearch(); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        const picked = suggestions[activeIdx];
        setQ(picked.icode);
        runSearch(picked.icode);
      } else {
        runSearch();
      }
    } else if (e.key === "Escape") {
      setShowSuggest(false);
    } else if (e.key === "Tab" && activeIdx >= 0) {
      setQ(suggestions[activeIdx].icode);
      setShowSuggest(false);
    }
  };

  const pickSuggestion = (s) => {
    setQ(s.icode);
    setShowSuggest(false);
    runSearch(s.icode);
  };

  // Highlight the delta between lowest and last so users see savings at a glance.
  const withDelta = useMemo(() => (items || []).map((it) => {
    const lo = it.lowest?.rate ?? null;
    const la = it.last?.rate ?? null;
    let delta = null;
    let delta_pct = null;
    if (lo != null && la != null && lo > 0) {
      delta = la - lo;
      delta_pct = ((la - lo) / lo) * 100;
    }
    return { ...it, delta, delta_pct };
  }), [items]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-cyan-800 to-cyan-700 text-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Row 1 — back-nav pills (compact on mobile) */}
          <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/sap")}
              className="text-white hover:bg-white/10 h-8 px-2"
              data-testid="price-explorer-back-sap">
              <ArrowLeft size={16} className="sm:mr-1" />
              <span className="hidden sm:inline">SAP Workspace</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/home")}
              className="text-white hover:bg-white/10 h-8 px-2"
              data-testid="price-explorer-back">
              <Home size={16} className="sm:mr-1" />
              <span className="hidden sm:inline">Home</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/supplier-price-list")}
              className="text-white hover:bg-white/10 h-8 px-2 ml-auto"
              data-testid="price-explorer-open-spl">
              <span className="hidden sm:inline">Supplier price list</span>
              <span className="inline sm:hidden">By supplier</span>
              <ArrowLeft size={14} className="ml-1 rotate-180" />
            </Button>
          </div>
          {/* Row 2 — page title, always full-width, wraps naturally */}
          <div>
            <h1 className="text-lg sm:text-xl font-bold">Price Explorer</h1>
            <p className="text-[11px] sm:text-xs text-cyan-100/90 leading-snug">
              Look up lowest &amp; last billed price for any item across all vendors
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Mode + source toolbar — pills that live above the search input.
            The `viewMode` toggle switches between the classic "one card
            per item with lowest/avg/last" (Item view) and the compact
            per-supplier comparison grid for a single item (Supplier view).
            The `sourceFilter` pills filter cards / rows client-side so
            the user can focus on MS SQL PO history OR SAP contracts
            without re-running the search. */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {/* Segmented control — view mode */}
          <div className="inline-flex bg-white border border-slate-300 rounded-md p-0.5"
               role="tablist" data-testid="pe-view-mode">
            {[
              { key: "item", label: "Item view" },
              { key: "supplier", label: "Supplier compare" },
            ].map((m) => (
              <button
                key={m.key}
                role="tab"
                aria-selected={viewMode === m.key}
                onClick={() => setViewMode(m.key)}
                data-testid={`pe-view-${m.key}`}
                className={`px-2.5 py-1 text-xs font-semibold rounded-sm transition-colors ${
                  viewMode === m.key
                    ? "bg-cyan-700 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}>
                {m.label}
              </button>
            ))}
          </div>
          {/* Source filter — 3-way pill; visible in both views */}
          <div className="inline-flex bg-white border border-slate-300 rounded-md p-0.5 ml-auto"
               role="tablist" data-testid="pe-source-filter">
            {[
              { key: "both", label: "Both" },
              { key: "mssql", label: "MS SQL" },
              { key: "sap", label: "SAP" },
            ].map((m) => (
              <button
                key={m.key}
                role="tab"
                aria-selected={sourceFilter === m.key}
                onClick={() => setSourceFilter(m.key)}
                data-testid={`pe-src-${m.key}`}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-sm transition-colors ${
                  sourceFilter === m.key
                    ? m.key === "mssql" ? "bg-cyan-700 text-white"
                    : m.key === "sap"   ? "bg-indigo-700 text-white"
                    :                     "bg-slate-800 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
          className="flex gap-2 mb-6 relative"
          ref={wrapperRef}
          data-testid="price-explorer-form"
        >
          <div className="flex-1 relative">
            <Input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggest(true)}
              onKeyDown={handleKeyDown}
              placeholder="Item code or name (e.g. SCR820WM or PHS Screw)…"
              className="w-full"
              data-testid="price-explorer-input"
              autoComplete="off"
              autoFocus
            />
            {/* Autosuggest dropdown. Absolute-positioned under the input;
                z-40 keeps it above BOTH the results grid AND the source
                legend banner (which was overlapping on mobile at z-20). */}
            {showSuggest && suggestions.length > 0 && (
              <ul
                className="absolute left-0 right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto"
                data-testid="price-explorer-suggest-list"
                role="listbox"
              >
                {suggestions.map((s, i) => (
                  <li
                    key={s.icode}
                    data-testid={`price-explorer-suggest-${s.icode}`}
                    role="option"
                    aria-selected={i === activeIdx}
                    // onMouseDown (not onClick) so it fires BEFORE the input
                    // loses focus and blur-dismisses the dropdown.
                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={
                      "px-3 py-2 cursor-pointer text-sm flex items-baseline gap-2 " +
                      (i === activeIdx ? "bg-cyan-50" : "hover:bg-slate-50")
                    }
                  >
                    <span className="font-mono text-xs font-bold text-cyan-800 min-w-[90px]">{s.icode}</span>
                    <span className="text-slate-700 flex-1 truncate">{s.iname || "(no description)"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Button type="submit" disabled={loading} data-testid="price-explorer-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            <span className="ml-1.5 hidden sm:inline">Search</span>
          </Button>
        </form>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 mb-4"
               data-testid="price-explorer-error">
            {error}
          </div>
        )}

        {items === null && !loading && (
          <div className="text-center text-slate-500 py-16" data-testid="price-explorer-empty">
            <IndianRupee size={40} className="mx-auto opacity-40 mb-3" />
            <p className="text-sm">
              Type an item code or name above to see its lowest &amp; last cost across vendors.
            </p>
            <p className="text-xs mt-2 text-slate-400">
              MS SQL PO history (last 180 days) + SAP list-price contracts, side by side.
            </p>
          </div>
        )}

        {items !== null && items.length === 0 && !loading && (
          <div className="text-center text-slate-500 py-12" data-testid="price-explorer-no-results">
            No items found matching &ldquo;{q}&rdquo;.
          </div>
        )}

        {/* ---- Supplier-compare view -------------------------------------
            Renders one row per supplier that either billed the item in the
            last 180 days OR holds a standing SAP list-price contract for
            it. Rows are pre-sorted cheapest-first by the API. Source
            pills filter columns and drop rows that have no data left.
        */}
        {viewMode === "supplier" && items !== null && items.length > 0 && (
          <div data-testid="pe-supplier-view">
            {supplierLoading && !supplierData && (
              <div className="text-sm text-slate-500 flex items-center gap-2 py-8 justify-center">
                <Loader2 size={14} className="animate-spin" />
                Loading supplier comparison…
              </div>
            )}
            {supplierData && (
              <SupplierCompareCard data={supplierData} sourceFilter={sourceFilter}
                                    onOpenHistory={setHistoryItem} />
            )}
          </div>
        )}

        {/* Source legend — shown whenever we have results so users understand
            which data comes from which system. Two clearly-labelled bands:
            MS SQL PO history (top three cards per item) and SAP list prices
            (contract rows below). */}
        {viewMode === "item" && items !== null && items.length > 0 && (
          <div className="mb-4 bg-white border border-slate-200 rounded-md p-3 text-xs text-slate-700 flex flex-wrap items-start gap-x-6 gap-y-2"
               data-testid="price-explorer-legend">
            <div className="flex items-start gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-cyan-100 text-cyan-800 shrink-0 mt-0.5">
                <BarChart3 size={12} />
              </span>
              <div>
                <div className="font-bold text-slate-900">MS SQL · PO history</div>
                <div className="text-slate-500">
                  What we <em>actually paid</em> — from billed <code>GPurci</code> rates in the last 180 days.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-indigo-100 text-indigo-800 shrink-0 mt-0.5">
                <Layers size={12} />
              </span>
              <div>
                <div className="font-bold text-slate-900">SAP · List price contracts</div>
                <div className="text-slate-500">
                  What we&apos;re <em>supposed to pay</em> — active/draft price contracts in SAP&nbsp;ByD.{" "}
                  {sapMeta?.age_minutes != null && (
                    <span className="text-slate-400">Synced {fmtStaleness(sapMeta.age_minutes)}.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`space-y-3 ${viewMode !== "item" ? "hidden" : ""}`}>
          {withDelta.map((it) => (
            <Card key={it.icode} className="border-slate-200" data-testid={`price-explorer-item-${it.icode}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-baseline gap-2 mb-3">
                  <span className="font-mono text-sm font-bold text-cyan-800">{it.icode}</span>
                  <span className="text-sm text-slate-700 flex-1 truncate">{it.iname || "(no description)"}</span>
                </div>

                <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${sourceFilter === "sap" ? "hidden" : ""}`}>
                  {/* Lowest cost card */}
                  <div className="border border-emerald-200 bg-emerald-50/50 rounded p-3"
                       data-testid={`price-explorer-lowest-${it.icode}`}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-700 font-bold mb-1">
                      <TrendingDown size={12} /> Lowest cost
                    </div>
                    {it.lowest ? (
                      <>
                        <div className="text-lg font-bold text-slate-900">{inr(it.lowest.rate)}</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {it.lowest.supplier || "—"}
                          {it.lowest.pcode && (
                            <span className="text-slate-400"> · {it.lowest.pcode}</span>
                          )}
                        </div>
                        <div className="text-fiori-caption text-slate-500 mt-0.5">
                          Bill dated {fmtDate(it.lowest.bill_date)}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-slate-500 italic">No purchase in this window</div>
                    )}
                  </div>

                  {/* Average cost card — qty-weighted + 6M WMA side by side.
                      Two numbers because they answer different questions:
                       • qty-weighted = "over the whole window, what was our
                         average cost per unit"
                       • 6M WMA      = "biased toward recent months so it
                         reacts to price shifts faster than a flat average" */}
                  <div className="border border-sky-200 bg-sky-50/50 rounded p-3"
                       data-testid={`price-explorer-average-${it.icode}`}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-sky-700 font-bold mb-1">
                      <BarChart3 size={12} /> Average cost
                    </div>
                    {it.average || it.wma ? (
                      <>
                        {(() => {
                          // Compute velocity once so both the WMA colour and
                          // the badge below use the exact same classification.
                          const vel = it.wma && it.average
                            ? classifyVelocity(it.average.rate, it.wma.rate, it.wma.bill_count)
                            : null;
                          const wmaColour = vel?.tone === "up"   ? "text-rose-700"
                                          : vel?.tone === "down" ? "text-emerald-700"
                                          : "text-slate-900";
                          return (
                            <>
                              {it.wma && (
                                <div className="mb-2" data-testid={`price-explorer-wma-${it.icode}`}>
                                  <div className={`text-lg font-bold ${wmaColour}`}>{inr(it.wma.rate)}</div>
                                  <div className="text-xs text-sky-700 font-semibold mt-0.5">
                                    6-mo weighted MA
                                  </div>
                                  <div className="text-fiori-caption text-slate-500 mt-0.5">
                                    Recent months weighted higher · {it.wma.bill_count} bill{it.wma.bill_count === 1 ? "" : "s"}
                                  </div>
                                  {vel && (
                                    <div className="mt-1.5" data-testid={`price-explorer-velocity-${it.icode}`}
                                         title={vel.tip}>
                                      {vel.tone === "up" && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-fiori-caption font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                                          <TrendingUp size={11} /> Trending up {vel.label}
                                        </span>
                                      )}
                                      {vel.tone === "down" && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-fiori-caption font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                                          <TrendingDown size={11} /> Trending down {vel.label}
                                        </span>
                                      )}
                                      {vel.tone === "stable" && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-fiori-caption font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                                          <Minus size={11} /> Stable
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {it.average && (
                                <div className={it.wma ? "pt-2 border-t border-sky-100" : ""}>
                                  <div className={it.wma ? "text-sm font-semibold text-slate-700" : "text-lg font-bold text-slate-900"}>
                                    {inr(it.average.rate)}
                                  </div>
                                  <div className="text-xs text-slate-600 mt-0.5">
                                    Qty-weighted (flat)
                                  </div>
                                  <div className="text-fiori-caption text-slate-500 mt-0.5">
                                    Over {it.average.bill_count} bill{it.average.bill_count === 1 ? "" : "s"}
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="text-xs text-slate-500 italic">No purchase in this window</div>
                    )}
                  </div>

                  {/* Last cost card */}
                  <div className="border border-slate-200 bg-white rounded p-3"
                       data-testid={`price-explorer-last-${it.icode}`}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-600 font-bold mb-1">
                      <Clock size={12} /> Last cost
                    </div>
                    {it.last ? (
                      <>
                        <div className="text-lg font-bold text-slate-900">{inr(it.last.rate)}</div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {it.last.supplier || "—"}
                          {it.last.pcode && (
                            <span className="text-slate-400"> · {it.last.pcode}</span>
                          )}
                        </div>
                        <div className="text-fiori-caption text-slate-500 mt-0.5">
                          Bill dated {fmtDate(it.last.bill_date)}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-slate-500 italic">No purchase in this window</div>
                    )}
                  </div>
                </div>

                {/* Delta pill — only show when we can compute both sides */}
                {it.delta != null && it.delta > 0 && (
                  <div className="mt-3 text-xs">
                    <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 px-2 py-0.5 rounded font-medium">
                      Last is {inr(it.delta)} higher than lowest
                      {it.delta_pct != null && (
                        <span className="text-amber-700"> ({it.delta_pct.toFixed(1)}%)</span>
                      )}
                    </span>
                  </div>
                )}
                {it.delta != null && it.delta <= 0 && (
                  <div className="mt-3 text-xs">
                    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded font-medium">
                      Last purchase was at (or below) the 180-day low
                    </span>
                  </div>
                )}

                {/* SAP list-price contracts — separate visual band below the
                    MS SQL cards so users see both sources side-by-side and
                    understand the difference (paid vs. contracted). Rows
                    already deduped + sorted cheapest-first by the API. */}
                <div className={`mt-4 pt-3 border-t border-slate-100 ${sourceFilter === "mssql" ? "hidden" : ""}`}
                     data-testid={`price-explorer-sap-${it.icode}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Layers size={13} className="text-indigo-700" />
                    <div className="text-xs uppercase tracking-wider font-bold text-indigo-800">
                      SAP list price contracts
                    </div>
                    <span className="text-fiori-caption text-slate-400" title="Standing contract prices in SAP BusinessByDesign, refreshed nightly.">
                      · standing prices from SAP&nbsp;ByD
                    </span>
                  </div>
                  {sapLoading && !sapByCode[it.icode] ? (
                    <div className="text-xs text-slate-500 italic flex items-center gap-1">
                      <Loader2 size={11} className="animate-spin" /> Loading SAP prices…
                    </div>
                  ) : (sapByCode[it.icode] || []).length === 0 ? (
                    <div className="text-xs text-slate-500 italic flex items-center gap-1.5">
                      <Info size={11} /> No active or draft list-price contract in SAP for this item.
                    </div>
                  ) : (
                    <div className="overflow-x-auto -mx-1">
                      <table className="w-full text-xs border border-indigo-100 rounded">
                        <thead>
                          <tr className="bg-indigo-50/60 text-indigo-900">
                            <th className="text-left px-2 py-1.5 font-semibold">Supplier</th>
                            <th className="text-right px-2 py-1.5 font-semibold">Price</th>
                            <th className="text-left px-2 py-1.5 font-semibold">UoM</th>
                            <th className="text-left px-2 py-1.5 font-semibold">Valid from</th>
                            <th className="text-left px-2 py-1.5 font-semibold">Valid to</th>
                            <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(sapByCode[it.icode] || []).map((r, i) => (
                            <tr key={`${r.supplier_code}-${i}`}
                                className="border-t border-indigo-50 hover:bg-indigo-50/30">
                              <td className="px-2 py-1.5">
                                <div className="font-mono text-fiori-caption font-bold text-slate-900">{r.supplier_code}</div>
                                <div className="text-fiori-caption text-slate-500 truncate">{r.supplier_name || "—"}</div>
                              </td>
                              <td className="px-2 py-1.5 text-right num font-semibold text-slate-900">
                                {r.amount == null ? "—" : `${Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 4 })} ${r.currency_code}`}
                              </td>
                              <td className="px-2 py-1.5 text-slate-600">/{r.base_quantity} {r.unit_code}</td>
                              <td className="px-2 py-1.5 text-slate-600">{fmtContractDate(r.valid_from)}</td>
                              <td className="px-2 py-1.5 text-slate-600">{fmtContractDate(r.valid_to)}</td>
                              <td className="px-2 py-1.5">
                                {r.release_status === "5" ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-fiori-caption font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">Released</span>
                                ) : r.release_status === "3" ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-fiori-caption font-semibold bg-amber-100 text-amber-800 border border-amber-200" title="A draft SAP list-price contract exists and is waiting to be released.">In preparation · awaiting approvals</span>
                                ) : (
                                  <span className="text-fiori-caption text-slate-500">{r.status_label || r.release_status || "—"}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Full-history opener — always available so users can drill
                    down into individual bills. We don't preload the drawer's
                    data here; the drawer fetches only when opened. */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setHistoryItem({ icode: it.icode, iname: it.iname })}
                    className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 underline underline-offset-2"
                    data-testid={`price-explorer-see-history-${it.icode}`}
                  >
                    See full purchase history →
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {/* Purchase history drawer — mounted at page root so a single instance
          serves all cards. Renders nothing until an item is selected. */}
      <PriceHistoryDrawer
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />
    </div>
  );
}
